import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import authClient from '../utils/authClient';
import { fetchIpLocations, mergeIpLocations } from '../utils/ipLocations';
import './FeedbackManagement.css';

const FEEDBACK_TYPES = {
  game_issue: '游戏问题',
  info_error: '信息错误',
  manual_request: '手册请求',
  new_game: '新游戏请求',
  recondb_entry: 'ReconDB 提交',
  site_bug: '网站 Bug',
  system_notice: '系统公告'
};

const STATUS_TYPES = {
  open: '待处理',
  replied: '已回复',
  delayed: '进行中',
  completed: '已完成',
  rejected: '暂不处理'
};

const formatIpLocation = (location, ipAddress) => {
  const formattedLocation = [location?.country, location?.regionName, location?.city, location?.district]
    .filter(Boolean)
    .filter((part, index, parts) => parts.indexOf(part) === index)
    .join(' · ');
  return formattedLocation || ipAddress || '未知位置';
};

const FeedbackManagement = ({ searchTerm = '' }) => {
  const navigate = useNavigate();
  const canAdminister = authClient.isAdmin();
  const [threads, setThreads] = useState([]);
  const [stats, setStats] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, totalPages: 1 });
  const [updatingIds, setUpdatingIds] = useState(() => new Set());
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newThread, setNewThread] = useState({
    type: 'system_notice',
    title: '',
    content: '',
    game_id: '',
    game_name: ''
  });

  useEffect(() => {
    if (!authClient.isAuthenticated()) navigate('/login');
  }, [navigate]);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Keep the first useful paint small. Older tickets remain available via
      // pagination instead of blocking the screen on the entire history.
      const params = { page, limit: 30 };
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      const response = await apiClient.get('/feedback/threads', { params });
      const pageThreads = response.data.threads || response.data || [];
      setThreads(Array.isArray(pageThreads) ? pageThreads : []);
      setPageInfo({
        total: Number(response.data.total || pageThreads.length),
        totalPages: Number(response.data.totalPages || 1)
      });
      fetchIpLocations(pageThreads)
        .then((locations) => setThreads((current) => mergeIpLocations(current, locations)))
        .catch((locationError) => console.error('Error loading IP locations:', locationError));
    } catch (requestError) {
      console.error('Error fetching feedback:', requestError);
      setThreads([]);
      setError('反馈加载失败，请稍后重试。');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, page]);

  const refreshStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/feedback/stats');
      setStats(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      console.error('Error fetching feedback stats:', requestError);
    }
  }, []);

  useEffect(() => {
    fetchThreads();
    refreshStats();
  }, [fetchThreads, refreshStats]);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, filterType]);

  useEffect(() => {
    apiClient.get('/games')
      .then((response) => setGames(Array.isArray(response.data) ? response.data : []))
      .catch((requestError) => console.error('Error fetching games:', requestError));
  }, []);

  const filteredThreads = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return threads;
    return threads.filter((thread) => [
      thread.id,
      thread.title,
      thread.game_name,
      FEEDBACK_TYPES[thread.type],
      thread.ip_address
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [searchTerm, threads]);

  const statusTotal = (status) => stats
    .filter((item) => item.status === status)
    .reduce((total, item) => total + Number(item.count || 0), 0);

  const updateThread = async (threadId, patch, request, failureMessage) => {
    const previous = threads.find((thread) => thread.id === threadId);
    setThreads((current) => current.map((thread) => (
      thread.id === threadId ? { ...thread, ...patch } : thread
    )));
    setUpdatingIds((current) => new Set(current).add(threadId));
    try {
      await request();
      refreshStats();
    } catch (requestError) {
      console.error(failureMessage, requestError);
      if (previous) {
        setThreads((current) => current.map((thread) => (
          thread.id === threadId ? previous : thread
        )));
      }
      alert(failureMessage);
    } finally {
      setUpdatingIds((current) => {
        const next = new Set(current);
        next.delete(threadId);
        return next;
      });
    }
  };

  const handleDelete = (threadId) => {
    if (!window.confirm('确定删除这条反馈及其全部回复吗？此操作不可撤销。')) return;
    const previous = threads;
    setThreads((current) => current.filter((thread) => thread.id !== threadId));
    apiClient.delete(`/feedback/threads/${threadId}`)
      .then(() => refreshStats())
      .catch((requestError) => {
        console.error('删除反馈失败', requestError);
        setThreads(previous);
        alert('删除反馈失败');
      });
  };

  const handleStatusChange = (threadId, status) => updateThread(
    threadId,
    { status },
    () => apiClient.put(`/feedback/threads/${threadId}/status`, { status }),
    '更新状态失败'
  );

  const handleGameSelect = (event) => {
    const gameId = event.target.value;
    const game = games.find((item) => item.id === Number(gameId));
    const gameName = game
      ? [game.titlechn, game.titleeng].filter(Boolean).join(' / ')
      : '';
    setNewThread((current) => ({ ...current, game_id: gameId, game_name: gameName }));
  };

  const handleNewThreadSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(newThread).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      formData.append('created_by_developer', '1');
      await apiClient.post('/feedback/threads', formData);
      setNewThread({ type: 'system_notice', title: '', content: '', game_id: '', game_name: '' });
      setShowNewThreadModal(false);
      await Promise.all([fetchThreads(), refreshStats()]);
    } catch (requestError) {
      console.error('Error creating thread:', requestError);
      alert('创建反馈失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (value) => new Date(value).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <section className="feedback-management">
      <header className="feedback-management__header">
        <div>
          <h2>反馈管理</h2>
        </div>
        {canAdminister && (
          <button className="feedback-primary-button" onClick={() => setShowNewThreadModal(true)}>
            <span aria-hidden="true">＋</span> 新建线程
          </button>
        )}
      </header>

      <div className="feedback-stats" aria-label="反馈统计">
        {Object.entries(STATUS_TYPES).map(([status, label]) => (
          <button
            key={status}
            className={`feedback-stat feedback-stat--${status} ${filterStatus === status ? 'is-active' : ''}`}
            onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
          >
            <strong>{statusTotal(status)}</strong>
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="feedback-toolbar">
        <div className="feedback-toolbar__group">
          <label>
            <span>类型</span>
            <select value={filterType} onChange={(event) => setFilterType(event.target.value)}>
              <option value="all">全部类型</option>
              {Object.entries(FEEDBACK_TYPES).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>状态</span>
            <select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
              <option value="all">全部状态</option>
              {Object.entries(STATUS_TYPES).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
        </div>
        <span className="feedback-result-count">共 {pageInfo.total} 条</span>
      </div>

      {loading ? (
        <div className="feedback-empty">正在加载反馈…</div>
      ) : error ? (
        <div className="feedback-empty feedback-empty--error">
          <span>{error}</span>
          <button onClick={fetchThreads}>重新加载</button>
        </div>
      ) : filteredThreads.length === 0 ? (
        <div className="feedback-empty">没有符合当前筛选条件的反馈。</div>
      ) : (
        <div className="feedback-admin-list">
          {filteredThreads.map((thread) => (
            <article
              key={thread.id}
              className={`feedback-admin-card ${thread.is_pinned === 1 ? 'is-pinned' : ''}`}
            >
              <Link className="feedback-card-main" style={{ textDecoration: 'none' }} to={`/manage-feedback-thread/${thread.id}`}>
                <div className="feedback-card-topline">
                  <span className="feedback-type-pill">{FEEDBACK_TYPES[thread.type] || thread.type}</span>
                  <span className={`feedback-status-pill feedback-status-pill--${thread.status}`}>
                    {STATUS_TYPES[thread.status] || thread.status}
                  </span>
                  {thread.is_pinned === 1 && <span className="feedback-pin-label">置顶</span>}
                </div>
                <h3>{thread.title}</h3>
                <p className="feedback-card-preview">{thread.content}</p>
                <div className="feedback-card-meta">
                  <span>#{thread.id}</span>
                  {thread.game_name && <span>{thread.game_name}</span>}
                  <span>{formatDate(thread.created_at)}</span>
                  <span>{thread.comment_count || 0} 条回复</span>
                  {thread.ip_address && (
                    <span title={thread.ip_address}>IP 位置：{formatIpLocation(thread.location, thread.ip_address)}</span>
                  )}
                </div>
              </Link>
              {canAdminister && <div className="feedback-card-actions">
                <select
                  aria-label={`更新反馈 ${thread.id} 状态`}
                  value={thread.status}
                  disabled={updatingIds.has(thread.id)}
                  onChange={(event) => handleStatusChange(thread.id, event.target.value)}
                >
                  {Object.entries(STATUS_TYPES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <button
                  className={thread.is_pinned === 1 ? 'is-active' : ''}
                  disabled={updatingIds.has(thread.id)}
                  onClick={() => updateThread(
                    thread.id,
                    { is_pinned: thread.is_pinned === 1 ? 0 : 1 },
                    () => apiClient.put(`/feedback/threads/${thread.id}/pin`, {
                      is_pinned: thread.is_pinned === 1 ? 0 : 1
                    }),
                    '更新置顶状态失败'
                  )}
                >
                  {thread.is_pinned === 1 ? '取消置顶' : '置顶'}
                </button>
                <button className="feedback-danger-button" onClick={() => handleDelete(thread.id)}>删除</button>
              </div>}
            </article>
          ))}
        </div>
      )}

      {!loading && !error && pageInfo.totalPages > 1 && (
        <nav className="pagination-container" aria-label="反馈分页">
          <button className="pagination-button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>上一页</button>
          <span>{page} / {pageInfo.totalPages}</span>
          <button className="pagination-button" disabled={page >= pageInfo.totalPages} onClick={() => setPage((current) => current + 1)}>下一页</button>
        </nav>
      )}

      {showNewThreadModal && (
        <div className="feedback-modal-overlay" onMouseDown={() => setShowNewThreadModal(false)}>
          <div className="feedback-admin-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div>
                <h2>新建反馈线程</h2>
              </div>
              <button type="button" onClick={() => setShowNewThreadModal(false)} aria-label="关闭">×</button>
            </header>
            <form onSubmit={handleNewThreadSubmit}>
              <label>
                <span>反馈类型</span>
                <select value={newThread.type} onChange={(event) => setNewThread({ ...newThread, type: event.target.value })}>
                  {Object.entries(FEEDBACK_TYPES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              {['game_issue', 'info_error', 'manual_request'].includes(newThread.type) && (
                <label>
                  <span>关联游戏</span>
                  <select value={newThread.game_id} onChange={handleGameSelect} required>
                    <option value="">请选择游戏</option>
                    {games.map((game) => (
                      <option key={game.id} value={game.id}>
                        {[game.titlechn, game.titleeng].filter(Boolean).join(' / ') || `Game ${game.id}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                <span>标题</span>
                <input
                  value={newThread.title}
                  onChange={(event) => setNewThread({ ...newThread, title: event.target.value })}
                  maxLength="255"
                  required
                />
              </label>
              <label>
                <span>内容</span>
                <textarea
                  value={newThread.content}
                  onChange={(event) => setNewThread({ ...newThread, content: event.target.value })}
                  rows="6"
                  required
                />
              </label>
              <footer>
                <button type="button" onClick={() => setShowNewThreadModal(false)}>取消</button>
                <button className="feedback-primary-button" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? '创建中…' : '创建线程'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default FeedbackManagement;
